import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { ENVIRONMENT_TOKEN } from "../types/environment.token";

const MAX_GET_FILTER_LENGTH = 1500;

@Injectable({
  providedIn: "root",
})
export class MongoClientService {
  private _http = inject(HttpClient);
  private _env = inject(ENVIRONMENT_TOKEN);
  private readonly _baseUrl: string;

  constructor() {
    this._baseUrl = `${this._env.apiUrl}/api/mongo`;
  }

  private _readsInBody(filterString: string): boolean {
    return this._env.mongoReadsInBody === true || filterString.length > MAX_GET_FILTER_LENGTH;
  }

  find<T>(
    database: string,
    collection: string,
    filter: any = {},
    options?: {
      sort?: any;
      limit?: number;
      skip?: number;
      projection?: any;
    },
  ): Observable<T[]> {
    const filterString = JSON.stringify(filter);
    const url = `${this._baseUrl}/${database}/${collection}/find`;

    if (this._readsInBody(filterString)) {
      return this._http.post<T[]>(url, {
        filter,
        ...options,
      });
    }

    const params: any = { filter: filterString };
    if (options?.sort) params.sort = JSON.stringify(options.sort);
    if (options?.limit) params.limit = options.limit.toString();
    if (options?.skip) params.skip = options.skip.toString();
    if (options?.projection)
      params.projection = JSON.stringify(options.projection);

    return this._http.get<T[]>(url, { params });
  }

  findOne<T>(
    database: string,
    collection: string,
    filter: any = {},
    projection?: any,
  ): Observable<T> {
    const filterString = JSON.stringify(filter);
    const url = `${this._baseUrl}/${database}/${collection}/findOne`;

    if (this._readsInBody(filterString)) {
      return this._http.post<T>(url, {
        filter,
        projection,
      });
    }

    const params: any = { filter: filterString, _t: Date.now() };
    if (projection) params.projection = JSON.stringify(projection);

    return this._http.get<T>(url, { params });
  }

  insertOne<T>(
    database: string,
    collection: string,
    document: T,
  ): Observable<any> {
    return this._http.post(
      `${this._baseUrl}/${database}/${collection}/insertOne`,
      { document },
    );
  }

  insertMany<T>(
    database: string,
    collection: string,
    documents: T[],
  ): Observable<any> {
    return this._http.post(
      `${this._baseUrl}/${database}/${collection}/insertMany`,
      { documents },
    );
  }

  updateOne(
    database: string,
    collection: string,
    filter: any,
    update: any,
    options?: { upsert?: boolean },
  ): Observable<any> {
    return this._http.post(
      `${this._baseUrl}/${database}/${collection}/updateOne`,
      { filter, update, options },
    );
  }

  updateMany(
    database: string,
    collection: string,
    filter: any,
    update: any,
    options?: { upsert?: boolean },
  ): Observable<any> {
    return this._http.post(
      `${this._baseUrl}/${database}/${collection}/updateMany`,
      { filter, update, options },
    );
  }

  replaceOne(
    database: string,
    collection: string,
    filter: any,
    replacement: any,
    options?: { upsert?: boolean },
  ): Observable<any> {
    return this._http.post(
      `${this._baseUrl}/${database}/${collection}/replaceOne`,
      { filter, replacement, options },
    );
  }

  deleteOne(
    database: string,
    collection: string,
    filter: any,
  ): Observable<any> {
    return this._http.post(
      `${this._baseUrl}/${database}/${collection}/deleteOne`,
      { filter },
    );
  }

  deleteMany(
    database: string,
    collection: string,
    filter: any,
  ): Observable<any> {
    return this._http.post(
      `${this._baseUrl}/${database}/${collection}/deleteMany`,
      { filter },
    );
  }

  aggregate<T>(
    database: string,
    collection: string,
    pipeline: any[],
  ): Observable<T[]> {
    return this._http.post<T[]>(
      `${this._baseUrl}/${database}/${collection}/aggregate`,
      { pipeline },
    );
  }

  count(
    database: string,
    collection: string,
    filter: any = {},
  ): Observable<{ count: number }> {
    const filterString = JSON.stringify(filter);
    const url = `${this._baseUrl}/${database}/${collection}/count`;

    if (this._readsInBody(filterString)) {
      return this._http.post<{ count: number }>(url, { filter });
    }

    return this._http.get<{ count: number }>(url, {
      params: { filter: filterString },
    });
  }

  estimatedCount(
    database: string,
    collection: string,
  ): Observable<{ count: number }> {
    return this._http.get<{ count: number }>(
      `${this._baseUrl}/${database}/${collection}/estimatedCount`,
    );
  }

  distinct<T>(
    database: string,
    collection: string,
    field: string,
    filter: any = {},
  ): Observable<T[]> {
    const filterString = JSON.stringify(filter);
    return this._http.get<T[]>(
      `${this._baseUrl}/${database}/${collection}/distinct/${field}`,
      { params: { filter: filterString } },
    );
  }
}
